// useMicCheck (LUM-82)
//
// Pre-call level check: open the selected mic, meter it on the
// /mic-test scale for up to MIC_CHECK_WINDOW_MS, pass the moment a
// voice-level frame arrives, report 'silent' if none does. The mic is
// released as soon as the check settles. Outcomes go to the existing
// mic_test_result breadcrumb (numbers and short enums only).

import { useCallback, useEffect, useRef, useState } from 'react';
import { track as breadcrumb } from '@/lib/telemetry/breadcrumb';
import { levelFromTimeDomain, MIC_HEARD_THRESHOLD } from '@/services/mic-level';

export type MicCheckStatus =
  | 'idle'
  | 'checking'
  | 'passed'
  | 'silent'
  | 'denied';

export type MicCheckOutcome = 'passed' | 'silent' | 'denied';

export const MIC_CHECK_WINDOW_MS = 3_000;
const MIC_CHECK_POLL_MS = 100;

const PERMISSION_ERRORS: Record<string, true> = {
  NotAllowedError: true,
  PermissionDeniedError: true,
  SecurityError: true,
};

export interface UseMicCheckReturn {
  status: MicCheckStatus;
  /** Live level while checking (0-255, /mic-test scale). */
  level: number;
  /** Device the latest passing check ran on; null unless passed. */
  passedDeviceId: string | null;
  run: (deviceId?: string) => Promise<MicCheckOutcome>;
}

interface ActiveCheck {
  stream: MediaStream | null;
  ctx: AudioContext | null;
  timer: number | undefined;
  settle: ((outcome: MicCheckOutcome) => void) | null;
}

function release(check: ActiveCheck): void {
  clearInterval(check.timer);
  check.timer = undefined;
  check.ctx?.close().catch(() => {
    // Context already closed — nothing to release.
  });
  check.ctx = null;
  if (check.stream) for (const track of check.stream.getTracks()) track.stop();
  check.stream = null;
}

/** Meter the check's stream on the /mic-test scale until a voiced
 *  frame (passed) or the end of the window (silent). Cancelling the
 *  check settles it as silent. */
async function meterStream(
  check: ActiveCheck,
  stream: MediaStream,
  onLevel: (level: number) => void,
): Promise<{ outcome: MicCheckOutcome; peak: number }> {
  const ctx = new window.AudioContext();
  check.ctx = ctx;
  if (ctx.state === 'suspended') await ctx.resume();
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 512;
  analyser.smoothingTimeConstant = 0.6;
  ctx.createMediaStreamSource(stream).connect(analyser);

  const { promise, resolve } = Promise.withResolvers<{
    outcome: MicCheckOutcome;
    peak: number;
  }>();
  const buf = new Uint8Array(analyser.fftSize);
  const startedAt = Date.now();
  let peak = 0;
  check.settle = outcome => resolve({ outcome, peak });
  check.timer = window.setInterval(() => {
    analyser.getByteTimeDomainData(buf);
    const v = levelFromTimeDomain(buf);
    if (v > peak) peak = v;
    onLevel(v);
    if (v >= MIC_HEARD_THRESHOLD) {
      resolve({ outcome: 'passed', peak });
    } else if (Date.now() - startedAt >= MIC_CHECK_WINDOW_MS) {
      resolve({ outcome: 'silent', peak });
    }
  }, MIC_CHECK_POLL_MS);
  return promise;
}

export function useMicCheck(): UseMicCheckReturn {
  const [status, setStatus] = useState<MicCheckStatus>('idle');
  const [level, setLevel] = useState(0);
  const [passedDeviceId, setPassedDeviceId] = useState<string | null>(null);
  const activeRef = useRef<ActiveCheck | null>(null);

  // A superseded or unmounted check settles as silent and lets go of
  // the mic — the browser's recording indicator must not linger.
  const cancelActive = useCallback(() => {
    const active = activeRef.current;
    activeRef.current = null;
    if (!active) return;
    release(active);
    active.settle?.('silent');
  }, []);

  useEffect(() => cancelActive, [cancelActive]);

  // Release the mic and publish the outcome — unless a newer check
  // superseded this one.
  const finish = useCallback(
    (
      check: ActiveCheck,
      deviceId: string | undefined,
      outcome: MicCheckOutcome,
      peak: number,
      errorName?: string,
    ): MicCheckOutcome => {
      release(check);
      if (activeRef.current !== check) return outcome;
      activeRef.current = null;
      setStatus(outcome);
      setLevel(0);
      if (outcome === 'passed') setPassedDeviceId(deviceId ?? '');
      void breadcrumb('mic_test_result', {
        outcome: `precall_${outcome}`,
        peak_level: peak,
        passed_threshold: outcome === 'passed',
        error_name: errorName,
      });
      return outcome;
    },
    [],
  );

  const run = useCallback(
    async (deviceId?: string): Promise<MicCheckOutcome> => {
      cancelActive();
      const check: ActiveCheck = {
        stream: null,
        ctx: null,
        timer: undefined,
        settle: null,
      };
      activeRef.current = check;
      setStatus('checking');
      setLevel(0);
      setPassedDeviceId(null);

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            // The browser default is never pinned as an exact device.
            deviceId:
              deviceId && deviceId !== 'default'
                ? { exact: deviceId }
                : undefined,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
          },
        });
      } catch (err) {
        // Missing/busy/overconstrained device: no level to be had.
        const name = err instanceof Error ? err.name : 'unknown';
        const outcome = PERMISSION_ERRORS[name] ? 'denied' : 'silent';
        return finish(check, deviceId, outcome, 0, name);
      }
      check.stream = stream;
      if (activeRef.current !== check) {
        return finish(check, deviceId, 'silent', 0);
      }

      // Without Web Audio there is nothing to measure; the call's own
      // audio engine needs it anyway and reports its own failure.
      if (typeof window.AudioContext !== 'function') {
        return finish(check, deviceId, 'passed', 0);
      }

      const { outcome, peak } = await meterStream(check, stream, setLevel);
      return finish(check, deviceId, outcome, peak);
    },
    [cancelActive, finish],
  );

  return { status, level, passedDeviceId, run };
}
